import React, { useCallback } from 'react'
import {
  Box,
  Checkbox,
  Input,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'

import { MIME_TYPES, Uploader } from '@/components/common/Uploader'
import { Card } from '@/components/Event/Detail/components/Card'
import { EventResultFields } from '@/components/Event/Feedback/EventResultFields'
import { TranscriptOrderInput } from '@/components/Event/Feedback/TranscriptOrderInput'
import { WitnessAttendees } from '@/components/Event/Feedback/WitnessAttendees'
import { DBFContact, TurnaroundHours } from '@/constants'
import { Event, ReporterFeedback } from '@/types'
import { shouldProvideTranscriptOrders } from '@/utils/parrot'

export const FeedbackWitnessForm: React.FC<{
  event: Event
  form: UseFormReturnType<ReporterFeedback>
  witnessIndex: number
}> = ({ event, form, witnessIndex }) => {
  const isHearing = event.type === 'hearing'

  const shouldProvideWitnessID = !isHearing

  const setFieldValue = useCallback(
    (field: string, value: any) => {
      form.setFieldValue(`events_feedbacks.${witnessIndex}.${field}`, value)
    },
    [form, witnessIndex],
  )

  const values = form.values.events_feedbacks?.[witnessIndex]

  return (
    <>
      <WitnessAttendees event={event} form={form} witnessIndex={witnessIndex} />
      {shouldProvideWitnessID && (
        <Card heading="Witness ID presented">
          <Stack>
            <TextInput
              label="ID number"
              error={form.errors[`events_feedbacks.${witnessIndex}.id_number`]}
              value={values?.id_number ?? ''}
              onChange={event =>
                setFieldValue('id_number', event.currentTarget.value)
              }
            />

            <Select
              label="ID type"
              withinPortal
              placeholder="Pick one"
              error={form.errors[`events_feedbacks.${witnessIndex}.id_type`]}
              value={values?.id_type}
              onChange={value => setFieldValue('id_type', value)}
              data={[
                {
                  value: 'drivers_license',
                  label: 'Drivers license',
                },
                {
                  value: 'passport',
                  label: 'Passport',
                },
                {
                  value: 'police_badge',
                  label: 'Police badge',
                },
                {
                  value: 'other',
                  label: 'Other',
                },
              ]}
              searchable
            />

            <Input.Wrapper
              id="input-screenshot"
              label="Screenshot"
              description="Supported are PNG, PDF, JPEG. Max size 10MB"
              error={
                form.errors[`events_feedbacks.${witnessIndex}.id_screenshots`]
              }
              className="mb-6"
            >
              <Uploader
                inputId="input-screenshot"
                accept={[MIME_TYPES.JPEG, MIME_TYPES.PNG, MIME_TYPES.PDF]}
                maxSize={10 * 10 ** 6} // 10MB
                presignUrl={event.presign_id_screenshot_upload_url}
                files={values?.id_screenshots}
                onChange={files => setFieldValue('id_screenshots', files)}
              />
            </Input.Wrapper>
          </Stack>
        </Card>
      )}
      <Card>
        <Stack>
          <Textarea
            label="Timestamp log of key events"
            error={
              form.errors[`events_feedbacks.${witnessIndex}.timestamp_log`]
            }
            value={values?.timestamp_log ?? ''}
            onChange={event =>
              setFieldValue('timestamp_log', event.currentTarget.value)
            }
            minRows={3}
            autosize
          />
        </Stack>
      </Card>
      {shouldProvideTranscriptOrders(event) && (
        <Card>
          <Stack>
            <Input.Wrapper
              error={
                form.errors[
                  `events_feedbacks.${witnessIndex}.witness_requested_transcript`
                ]
              }
            >
              <Checkbox
                label="Witness choose to read the transcript"
                checked={values?.witness_requested_transcript}
                onChange={event =>
                  setFieldValue(
                    'witness_requested_transcript',
                    event.currentTarget.checked,
                  )
                }
              />
            </Input.Wrapper>

            <Checkbox
              label={
                event.can_order_for_oc
                  ? 'Are there any certified transcript orders at this time?'
                  : `${DBFContact.NAME} client ordered transcript`
              }
              checked={values?.transcript_ordered}
              onChange={event => {
                setFieldValue('transcript_orders', null)

                setFieldValue('transcript_ordered', event.currentTarget.checked)
              }}
              error={
                form.errors[
                  `events_feedbacks.${witnessIndex}.transcript_ordered`
                ]
              }
            />

            {values?.transcript_ordered && (
              <Box mt="md">
                <TranscriptOrderInput
                  form={form}
                  witnessIndex={witnessIndex}
                  withOcOrdering={!!event.can_order_for_oc}
                  attendees={values.attendees}
                  defaultTurnaroundHours={
                    event.is_transcript_order_required
                      ? TurnaroundHours.SEVEN_DAYS
                      : TurnaroundHours.ROUGH_DRAFT
                  }
                  withRoughDraftOption={!event.is_transcript_order_required}
                  // for court reporter events all attorneys must order transcript
                  // for other events only some attorneys can order transcript
                  clearable={
                    !!event.can_order_for_oc &&
                    !event.is_transcript_order_required
                  }
                />
              </Box>
            )}
          </Stack>
        </Card>
      )}

      <Card>
        <Stack>
          <EventResultFields
            event={event}
            form={form}
            witnessIndex={witnessIndex}
          />
          <Textarea
            label="How was the event? Any notes?"
            error={
              form.errors[`events_feedbacks.${witnessIndex}.additional_notes`]
            }
            value={values?.additional_notes ?? ''}
            onChange={event =>
              setFieldValue('additional_notes', event.currentTarget.value)
            }
            minRows={3}
            autosize
          />
        </Stack>
      </Card>
    </>
  )
}
